/**
 * CrudForm — reusable themed create/edit field-set for the generic-CRUD
 * admin pages (OIDCPAM Gate-6 Phase 4a). Field-config driven
 * (text/password/select/textarea/checkbox/urilist), intentionally matching
 * the shape of the pre-migration MUI-based
 * `ui/src/resource/ResourceForm.tsx`'s `Field`/`FieldOption` + its
 * `UriListField.tsx` companion, so a page's existing `fields` array migrates
 * with only an import-path/type-name rename — the required-field
 * validation logic (`buildInitialValues`/`computeErrors`) is carried over
 * unchanged from `ResourceForm.tsx` (already correct, proven by its
 * existing test coverage).
 *
 * Native <select>/<input type="checkbox"> replace MUI's popover Select and
 * custom Checkbox, so field interaction in tests becomes a plain
 * `fireEvent.change`/`fireEvent.click` rather than MUI's
 * mouseDown+option-click pattern.
 *
 * All visuals come from `@devnw/theme`'s `asUnified`/`ut` tokens (44px
 * inputs, `:focus-visible` outlines) — no MUI, no hardcoded colors except
 * literal fallbacks for optional-chained theme reads (same defensive
 * pattern as PamStateComponents.tsx).
 */
import React, {
  useCallback,
  useState,
  ChangeEvent,
  FormEvent,
  KeyboardEvent,
} from 'react'
import styled, { css } from 'styled-components'
import { asUnified, ut } from '@devnw/theme'
import { Icon } from '@devnw/icons'
import { CrudPrimaryButton, CrudSecondaryButton } from './CrudButtons'

export interface CrudFieldOption {
  value: string
  label: string
}

export interface CrudField {
  name: string
  label: string
  type?: 'text' | 'password' | 'select' | 'textarea' | 'checkbox' | 'urilist'
  options?: CrudFieldOption[]
  required?: boolean
  /** Per-entry validator for urilist fields; returns an error string or null. */
  validateEntry?: (value: string) => string | null
  /** Placeholder text (text/password/textarea, and the urilist pending-entry input). */
  placeholder?: string
}

export interface CrudFormProps {
  fields: CrudField[]
  initial?: Record<string, unknown>
  onSubmit: (values: Record<string, unknown>) => void | Promise<void>
  submitLabel?: string
}

// ---------------------------------------------------------------------------
// Styled atoms
// ---------------------------------------------------------------------------

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => ut(asUnified(theme)).space('md')};
  align-items: flex-start;
`

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => ut(asUnified(theme)).space('xxs')};
  align-self: stretch;
`

const Label = styled.label`
  font-size: ${({ theme }) => asUnified(theme).font?.sizes?.sm ?? '0.875rem'};
  font-weight: 600;
  color: ${({ theme }) => asUnified(theme).text?.primary ?? '#0f172a'};
`

const inputBase = css`
  min-height: 44px;
  padding: ${({ theme }) =>
    `${ut(asUnified(theme)).space('xs')} ${ut(asUnified(theme)).space('sm')}`};
  font-size: ${({ theme }) => asUnified(theme).font?.sizes?.sm ?? '0.875rem'};
  font-family: inherit;
  color: ${({ theme }) => asUnified(theme).text?.primary ?? '#0f172a'};
  background: ${({ theme }) => asUnified(theme).bg?.canvas ?? '#ffffff'};
  border: 1px solid ${({ theme }) => ut(asUnified(theme)).borderColor() ?? '#cbd5e1'};
  border-radius: ${({ theme }) => ut(asUnified(theme)).radius() ?? 4}px;

  &:focus-visible {
    outline: 2px solid ${({ theme }) => asUnified(theme).colors?.primary ?? '#1a56db'};
    outline-offset: 1px;
  }

  &:disabled {
    opacity: 0.5;
  }
`

const Input = styled.input`
  ${inputBase}
  align-self: stretch;
`

const TextArea = styled.textarea`
  ${inputBase}
  align-self: stretch;
  min-height: 88px;
  resize: vertical;
`

const Select = styled.select`
  ${inputBase}
  align-self: stretch;
`

const CheckLabel = styled.label`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => ut(asUnified(theme)).space('xs')};
  min-height: 44px;
  font-size: ${({ theme }) => asUnified(theme).font?.sizes?.sm ?? '0.875rem'};
  font-weight: 600;
  color: ${({ theme }) => asUnified(theme).text?.primary ?? '#0f172a'};
  cursor: pointer;
`

const HelperError = styled.span`
  font-size: ${({ theme }) => asUnified(theme).font?.sizes?.xs ?? '0.75rem'};
  color: ${({ theme }) => asUnified(theme).colors?.danger ?? '#991b1b'};
`

const UriListRow = styled.div`
  display: flex;
  gap: ${({ theme }) => ut(asUnified(theme)).space('xs')};
  align-self: stretch;
`

const UriListInput = styled(Input)`
  flex: 1 1 auto;
  align-self: auto;
`

const ChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => ut(asUnified(theme)).space('xs')};
`

const Chip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => ut(asUnified(theme)).space('xxs')};
  padding: ${({ theme }) =>
    `${ut(asUnified(theme)).space('xxs')} ${ut(asUnified(theme)).space('xs')}`};
  border-radius: ${({ theme }) => ut(asUnified(theme)).radius() ?? 4}px;
  background: ${({ theme }) => asUnified(theme).bg?.subtle ?? asUnified(theme).bg?.surface ?? '#f1f5f9'};
  font-size: ${({ theme }) => asUnified(theme).font?.sizes?.xs ?? '0.75rem'};
  color: ${({ theme }) => asUnified(theme).text?.primary ?? '#0f172a'};
  max-width: 100%;
`

const ChipText = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 320px;
`

const ChipRemoveButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: none;
  color: inherit;
  cursor: pointer;
  flex-shrink: 0;

  &:hover {
    background: ${({ theme }) => `${asUnified(theme).colors?.danger ?? '#991b1b'}1a`};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => asUnified(theme).colors?.primary ?? '#1a56db'};
    outline-offset: 1px;
  }
`

// ---------------------------------------------------------------------------
// urilist field — themed equivalent of ui/src/resource/UriListField.tsx
// ---------------------------------------------------------------------------

interface CrudUriListFieldProps {
  label: string
  placeholder?: string
  value: string[]
  error?: string
  onChange: (next: string[]) => void
  validateEntry?: (raw: string) => string | null
}

const CrudUriListField = ({
  label,
  placeholder,
  value,
  error,
  onChange,
  validateEntry,
}: CrudUriListFieldProps): JSX.Element => {
  const [pending, setPending] = useState('')
  const [entryError, setEntryError] = useState<string | null>(null)

  const handleAdd = (): void => {
    const trimmed = pending.trim()
    if (!trimmed) return

    if (value.includes(trimmed)) {
      setEntryError('Already added')
      return
    }

    if (validateEntry) {
      const validationErr = validateEntry(trimmed)
      if (validationErr !== null) {
        setEntryError(validationErr)
        return
      }
    }

    onChange([...value, trimmed])
    setPending('')
    setEntryError(null)
  }

  const handleRemove = (uri: string): void => {
    onChange(value.filter((u) => u !== uri))
  }

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleAdd()
    }
  }

  const displayError = entryError ?? (value.length === 0 ? error : undefined)

  return (
    <FieldGroup>
      <Label as="span">{label}</Label>
      <UriListRow>
        <UriListInput
          type="text"
          value={pending}
          placeholder={placeholder}
          aria-label={label}
          onChange={(e) => {
            setPending(e.target.value)
            setEntryError(null)
          }}
          onKeyDown={handleKeyDown}
        />
        <CrudSecondaryButton type="button" onClick={handleAdd}>
          Add
        </CrudSecondaryButton>
      </UriListRow>
      {displayError && <HelperError>{displayError}</HelperError>}
      {value.length > 0 && (
        <ChipRow>
          {value.map((uri) => (
            <Chip key={uri}>
              <ChipText>{uri}</ChipText>
              <ChipRemoveButton
                type="button"
                aria-label={`Remove ${uri}`}
                onClick={() => handleRemove(uri)}
              >
                <Icon name="xmark" ariaHidden size="xs" />
              </ChipRemoveButton>
            </Chip>
          ))}
        </ChipRow>
      )}
    </FieldGroup>
  )
}

// ---------------------------------------------------------------------------
// Validation helpers (ported unchanged from ResourceForm.tsx)
// ---------------------------------------------------------------------------

const buildInitialValues = (
  fields: CrudField[],
  initial?: Record<string, unknown>
): Record<string, unknown> =>
  fields.reduce<Record<string, unknown>>((acc, f) => {
    let val: unknown
    if (initial && f.name in initial) {
      val = initial[f.name]
    } else if (f.type === 'checkbox') {
      val = false
    } else if (f.type === 'urilist') {
      val = []
    } else {
      val = ''
    }
    return { ...acc, [f.name]: val }
  }, {})

const computeErrors = (
  fields: CrudField[],
  values: Record<string, unknown>
): Record<string, string> =>
  fields
    .filter((f) => Boolean(f.required) && f.type !== 'checkbox')
    .reduce<Record<string, string>>((acc, f) => {
      const val = values[f.name]
      if (f.type === 'urilist') {
        if (!Array.isArray(val) || val.length === 0) {
          return { ...acc, [f.name]: `${f.label} is required` }
        }
        return acc
      }
      if (val === '' || val === null || val === undefined) {
        return { ...acc, [f.name]: `${f.label} is required` }
      }
      return acc
    }, {})

// ---------------------------------------------------------------------------
// CrudForm
// ---------------------------------------------------------------------------

export const CrudForm = ({
  fields,
  initial,
  onSubmit,
  submitLabel = 'Submit',
}: CrudFormProps): JSX.Element => {
  const [values, setValues] = useState<Record<string, unknown>>(() =>
    buildInitialValues(fields, initial)
  )
  const [touched, setTouched] = useState(false)

  const errors = touched ? computeErrors(fields, values) : {}
  const hasErrors = Object.keys(computeErrors(fields, values)).length > 0

  const handleChange = useCallback(
    (
      e: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
    ) => {
      const { name, type } = e.target
      const value =
        type === 'checkbox'
          ? (e.target as HTMLInputElement).checked
          : e.target.value
      setValues((prev) => ({ ...prev, [name]: value }))
    },
    []
  )

  const handleSubmit = useCallback(
    async (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault()
      setTouched(true)
      if (hasErrors) return
      await onSubmit(values)
    },
    [hasErrors, onSubmit, values]
  )

  return (
    <Form onSubmit={handleSubmit} noValidate>
      {fields.map((f) => {
        const fieldType = f.type ?? 'text'
        const id = `cf-${f.name}`
        const err = errors[f.name]

        if (fieldType === 'checkbox') {
          return (
            <CheckLabel key={f.name} htmlFor={id}>
              <input
                id={id}
                name={f.name}
                type="checkbox"
                checked={Boolean(values[f.name])}
                onChange={handleChange}
              />
              {f.label}
            </CheckLabel>
          )
        }

        if (fieldType === 'select') {
          return (
            <FieldGroup key={f.name}>
              <Label htmlFor={id}>{f.label}</Label>
              <Select
                id={id}
                name={f.name}
                value={String(values[f.name] ?? '')}
                onChange={handleChange}
                aria-invalid={Boolean(err)}
              >
                {(f.options ?? []).map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </Select>
              {err && <HelperError>{err}</HelperError>}
            </FieldGroup>
          )
        }

        if (fieldType === 'urilist') {
          const uriValue = Array.isArray(values[f.name])
            ? (values[f.name] as string[])
            : []
          return (
            <CrudUriListField
              key={f.name}
              label={f.label}
              placeholder={f.placeholder}
              value={uriValue}
              error={err}
              onChange={(next) => setValues((prev) => ({ ...prev, [f.name]: next }))}
              validateEntry={f.validateEntry}
            />
          )
        }

        if (fieldType === 'textarea') {
          return (
            <FieldGroup key={f.name}>
              <Label htmlFor={id}>{f.label}</Label>
              <TextArea
                id={id}
                name={f.name}
                value={String(values[f.name] ?? '')}
                onChange={handleChange}
                placeholder={f.placeholder}
                aria-invalid={Boolean(err)}
              />
              {err && <HelperError>{err}</HelperError>}
            </FieldGroup>
          )
        }

        return (
          <FieldGroup key={f.name}>
            <Label htmlFor={id}>{f.label}</Label>
            <Input
              id={id}
              name={f.name}
              type={fieldType}
              value={String(values[f.name] ?? '')}
              onChange={handleChange}
              placeholder={f.placeholder}
              aria-invalid={Boolean(err)}
            />
            {err && <HelperError>{err}</HelperError>}
          </FieldGroup>
        )
      })}

      <CrudPrimaryButton type="submit">{submitLabel}</CrudPrimaryButton>
    </Form>
  )
}
